import { isAfter, isBefore, parseISO } from 'date-fns';
import { HPSummarySection } from 'enums/hitter-profile.enums';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { HitterProfileHelper } from 'lib_ts/classes/hitter-profile.helper';
import { HitterProfilePeriod } from 'lib_ts/enums/hitters.enums';
import { IHitter } from 'lib_ts/interfaces/i-hitter';
import {
  IHitterProfileHitsFilter,
  IHitterProfileSummaryFilter,
} from 'lib_ts/interfaces/i-hitter-profile';
import { ISessionHit } from 'lib_ts/interfaces/session-hits/i-session-hit';
import { isNil } from 'lodash';
import { HittersService } from 'services/hitters.service';
import { create } from 'zustand';

type HitsState = {
  loading: boolean;
  // controls which hits to summarize
  hitter: IHitter | undefined;
  // loaded based on hitsFilter
  hits: ISessionHit[];
  hitsFilter: IHitterProfileHitsFilter;
};

type HitsActions = {
  setHitter: (value: IHitter | undefined) => void;
  mergeHitsFilter: (
    value: Partial<IHitterProfileHitsFilter>,
    silently?: boolean
  ) => Promise<void>;
};

type SummaryState = {
  currSummaryHits: ISessionHit[]; // hits after applying summary filter for current period
  prevSummaryHits: ISessionHit[]; // hits after applying summary filter for previous period
  summaryFilter: IHitterProfileSummaryFilter;
  // controls which widgets to display
  summarySections: HPSummarySection[];
};

type SummaryActions = {
  mergeSummaryFilter: (
    value: Partial<IHitterProfileSummaryFilter>
  ) => Promise<void>;

  setSummarySections: (value: HPSummarySection[]) => void;
  resetFilters: () => void;
};

type HitterProfileState = HitsState & SummaryState;
type HitterProfileActions = HitsActions & SummaryActions;
type HitterProfileStore = HitterProfileState & HitterProfileActions;

const applySummaryFilter = (
  hits: ISessionHit[],
  filter: IHitterProfileSummaryFilter
) =>
  hits
    // note - we filter out invalid hits in the Key Metrics and Hit Spray chart Hitter Profile sections but leave them in the total BP and Pitch Types faced sections
    .filter((h) => !h.deleted)
    .filter(
      (h) =>
        isNil(filter.pitcherHand) ||
        filter.pitcherHand === BallHelper.getPitcherHand(h.pitch?.release?.px)
    )
    .filter(
      (h) =>
        filter.pitchTypes.length === 0 ||
        (h.pitch?.type && filter.pitchTypes.includes(h.pitch.type))
    );

const defaultHitsFilter: IHitterProfileHitsFilter = {
  hitterID: '',
  period: HitterProfilePeriod.Last3Months,
};

const defaultSummaryFilter: IHitterProfileSummaryFilter = {
  // * note: nothing selected => everything selected
  pitchTypes: [],
  pitcherHand: null,
};

const useHitterProfileStore = create<HitterProfileStore>((set, get) => ({
  loading: true,
  hitter: undefined,

  hits: [],
  hitsFilter: defaultHitsFilter,

  currSummaryHits: [],
  prevSummaryHits: [],

  summaryFilter: defaultSummaryFilter,

  // * note: nothing selected => everything selected
  summarySections: [],

  setHitter: async (value) => set({ hitter: value }),

  mergeHitsFilter: async (value, silently) => {
    const { hitsFilter, summaryFilter, loading } = get();

    const nextFilter: IHitterProfileHitsFilter = {
      ...hitsFilter,
      ...value,
    };

    if (!nextFilter.hitterID) {
      // clear hits but don't load anything
      set({
        hitsFilter: nextFilter,
        hits: [],
        currSummaryHits: [],
        prevSummaryHits: [],
      });
      return;
    }

    // clear hits before starting the load
    set({
      hitsFilter: nextFilter,
      hits: [],
      currSummaryHits: [],
      prevSummaryHits: [],
      // leave loading alone if silent
      loading: silently ? loading : true,
    });

    const nextHits =
      await HittersService.getInstance().getProfileHits(nextFilter);

    const summaryHits = applySummaryFilter(nextHits, summaryFilter);

    const currentStart = HitterProfileHelper.getCurrentStartDate(
      nextFilter.period
    );

    const currSummaryHits = summaryHits.filter((h) =>
      isAfter(parseISO(h._created), currentStart)
    );

    const prevSummaryHits = summaryHits.filter((h) =>
      isBefore(
        parseISO(h._created),
        HitterProfileHelper.getPreviousEndDate(nextFilter.period)
      )
    );

    if (nextHits.length === 0) {
      console.warn('No hits satisfy the hits filter criteria', nextFilter);
    }

    set({
      hits: nextHits,
      currSummaryHits,
      prevSummaryHits,
      // leave loading alone if silent
      loading: silently ? loading : false,
    });
  },

  mergeSummaryFilter: async (value) => {
    const { hits, summaryFilter, hitsFilter } = get();

    const nextFilter: IHitterProfileSummaryFilter = {
      ...summaryFilter,
      ...value,
    };

    const nextSummaryHits = applySummaryFilter(hits, nextFilter);

    if (hits.length > 0 && nextSummaryHits.length === 0) {
      console.warn('No hits satisfy the summary filter criteria', nextFilter);
    }

    const currentStart = HitterProfileHelper.getCurrentStartDate(
      hitsFilter.period
    );

    const currSummaryHits = nextSummaryHits.filter((h) =>
      isAfter(parseISO(h._created), currentStart)
    );

    const prevSummaryHits = nextSummaryHits.filter((h) =>
      isBefore(
        parseISO(h._created),
        HitterProfileHelper.getPreviousEndDate(hitsFilter.period)
      )
    );

    // re-apply the latest filters to whatever hits we have loaded
    set({
      summaryFilter: nextFilter,
      currSummaryHits,
      prevSummaryHits,
    });
  },

  setSummarySections: (value) => set({ summarySections: value }),

  resetFilters: async () => {
    const { mergeHitsFilter, mergeSummaryFilter } = get();

    await mergeHitsFilter({
      period: defaultHitsFilter.period,
    });

    mergeSummaryFilter(defaultSummaryFilter);
  },
}));

export default useHitterProfileStore;
